import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function Sobre() {
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleProjectsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/');
    setTimeout(() => {
      const projectsSection = document.getElementById('projetos');
      if (projectsSection) {
        projectsSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const handleContactClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/');
    setTimeout(() => {
      const contactSection = document.getElementById('contato');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar scrolled={scrolled} onProjectsClick={handleProjectsClick} onContactClick={handleContactClick} />
      
      <section className="pt-32 pb-20 bg-sand">
        <div className="max-w-[1200px] mx-auto px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="font-playfair text-6xl lg:text-7xl font-light text-gray-900 mb-6">
              Sobre o <strong className="font-bold">Criarquitetura</strong>
            </h1>
            <p className="font-inter text-xl text-gray-600 max-w-3xl leading-relaxed">
              Estúdio de arquitetura em Ilhabela especializado em projetos residenciais contemporâneos e arquitetura litorânea de alto padrão.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-[1200px] mx-auto px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="font-playfair text-4xl text-gray-900 mb-6">
                Arquitetura autoral para o litoral
              </h2>
              <p className="font-inter text-base text-gray-700 leading-relaxed mb-6">
                O Criarquitetura nasceu da paixão por criar residências únicas que dialogam com a paisagem litorânea de Ilhabela. Cada projeto é desenvolvido com atenção especial às características do terreno, ao clima e ao estilo de vida de quem escolhe viver no litoral.
              </p>
              <p className="font-inter text-base text-gray-700 leading-relaxed mb-6">
                Atuamos desde o conceito arquitetônico até o acompanhamento de obra, oferecendo soluções completas para clientes que buscam qualidade técnica, estética atemporal e segurança no processo construtivo.
              </p>
              <p className="font-inter text-base text-gray-700 leading-relaxed">
                Nossa abordagem combina conhecimento técnico específico sobre construção em áreas litorâneas com sensibilidade estética para criar casas que valorizam a vista, aproveitam a ventilação natural e utilizam materiais adequados ao clima de Ilhabela.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="rounded-3xl overflow-hidden"
            >
              <img
                src="https://static.readdy.ai/image/c30d1200a69d27266a785c8d79b381ff/b080888053ed06798dd6afa3f869e3ef.png"
                alt="Estúdio Criarquitetura - Arquitetos em Ilhabela"
                className="w-full h-full object-cover object-top"
              />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-sand">
        <div className="max-w-[1200px] mx-auto px-8">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="font-playfair text-4xl text-gray-900 mb-16 text-center"
          >
            Nossos Diferenciais
          </motion.h2>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-white p-10 rounded-2xl"
            >
              <div className="w-14 h-14 flex items-center justify-center bg-accent/10 rounded-xl mb-6">
                <i className="ri-map-2-line text-3xl text-accent"></i>
              </div>
              <h3 className="font-inter font-semibold text-xl text-gray-900 mb-4">
                Conhecimento Local
              </h3>
              <p className="font-inter text-sm text-gray-700 leading-relaxed">
                Experiência específica em projetos para Ilhabela, conhecendo a legislação local, características climáticas e desafios construtivos da região.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-white p-10 rounded-2xl"
            >
              <div className="w-14 h-14 flex items-center justify-center bg-accent/10 rounded-xl mb-6">
                <i className="ri-pencil-ruler-2-line text-3xl text-accent"></i>
              </div>
              <h3 className="font-inter font-semibold text-xl text-gray-900 mb-4">
                Projetos Personalizados
              </h3>
              <p className="font-inter text-sm text-gray-700 leading-relaxed">
                Cada projeto é único e desenvolvido especialmente para o cliente, considerando suas necessidades, estilo de vida e características do terreno.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white p-10 rounded-2xl"
            >
              <div className="w-14 h-14 flex items-center justify-center bg-accent/10 rounded-xl mb-6">
                <i className="ri-shield-check-line text-3xl text-accent"></i>
              </div>
              <h3 className="font-inter font-semibold text-xl text-gray-900 mb-4">
                Acompanhamento Completo
              </h3>
              <p className="font-inter text-sm text-gray-700 leading-relaxed">
                Suporte técnico durante toda a obra, garantindo que o projeto seja executado com qualidade e fidelidade ao planejamento original.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-[1200px] mx-auto px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-playfair text-4xl text-gray-900 mb-6">
              Vamos conversar sobre seu projeto?
            </h2>
            <p className="font-inter text-lg text-gray-600 mb-10 max-w-2xl mx-auto">
              Entre em contato para agendar uma conversa e conhecer como podemos transformar seu terreno em Ilhabela em uma residência única.
            </p>
            <a
              href="https://wa.me/5512999999999"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-10 py-4 bg-accent text-white text-base font-inter font-medium rounded-full hover:bg-accent/90 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line text-xl"></i>
              Falar com Arquiteto
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
